import * as React from "react";
import type { Patient } from "../../core/services/patients.service.ts";
import { Interactions } from "../../core/providers/interactions.provider.tsx";
import { patientNameParts } from "./patient-name.ts";
import { I } from "../icons/icons.tsx";

const COURRIER_TEMPLATES = [
  "Lettre de bienvenue",
  "Relance de rendez-vous",
  "Compte-rendu de consultation",
  "Devis de traitement",
] as const;

function patientEmail(p: Patient): string {
  const { first, last } = patientNameParts(p.name);
  return `${(first || "patient").toLowerCase()}.${(last || "x").toLowerCase()}@email.fr`;
}

export function PatientQuickActions({ patient }: { patient: Patient }) {
  const toast = Interactions.useToast();
  const [courrierOpen, setCourrierOpen] = React.useState(false);
  const courrierRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!courrierOpen) return;
    const onClickAway = (e: MouseEvent) => {
      if (!courrierRef.current?.contains(e.target as Node)) {
        setCourrierOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [courrierOpen]);

  const email = patientEmail(patient);

  return (
    <div className="grid grid-cols-4 gap-1.5">
      <button
        type="button"
        title="Appeler"
        aria-label="Appeler le patient"
        className="inline-flex h-9 items-center justify-center rounded-md border border-sf-border bg-white text-sf-text-secondary hover:bg-sf-primary-50 hover:text-sf-primary active:scale-[0.98]"
        onClick={() => {
          window.location.href = `tel:${patient.phone.replace(/\s+/g, "")}`;
          toast.push({ type: "info", title: `Appel de ${patient.name}` });
        }}
      >
        <I.Phone size={15} />
      </button>
      <button
        type="button"
        title="Envoyer un email"
        aria-label="Envoyer un email au patient"
        className="inline-flex h-9 items-center justify-center rounded-md border border-sf-border bg-white text-sf-text-secondary hover:bg-sf-primary-50 hover:text-sf-primary active:scale-[0.98]"
        onClick={() => {
          window.location.href = `mailto:${email}`;
          toast.push({ type: "success", title: "Brouillon d'email ouvert" });
        }}
      >
        <I.Mail size={15} />
      </button>
      <button
        type="button"
        title="Envoyer une notification"
        aria-label="Envoyer une notification au patient"
        className="inline-flex h-9 items-center justify-center rounded-md border border-sf-border bg-white text-sf-text-secondary hover:bg-sf-primary-50 hover:text-sf-primary active:scale-[0.98]"
        onClick={() =>
          toast.push({
            type: "success",
            title: "Notification envoyée",
            message: patient.name,
          })
        }
      >
        <I.Bell size={15} />
      </button>
      <div ref={courrierRef} className="relative">
        <button
          type="button"
          title="Générer un courrier"
          aria-label="Générer un courrier"
          className="inline-flex h-9 w-full items-center justify-center rounded-md border border-sf-border bg-white text-sf-text-secondary hover:bg-sf-primary-50 hover:text-sf-primary active:scale-[0.98]"
          onClick={() => setCourrierOpen((v) => !v)}
        >
          <I.FileText size={15} />
        </button>
        {courrierOpen && (
          <div className="absolute top-full right-0 z-50 mt-1 w-52 overflow-hidden rounded-lg border border-sf-border bg-white shadow-lg">
            <div className="border-b border-sf-border px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-wider text-sf-text-muted">
              Modèles de courrier
            </div>
            {COURRIER_TEMPLATES.map((tpl) => (
              <button
                key={tpl}
                type="button"
                onClick={() => {
                  setCourrierOpen(false);
                  toast.push({
                    type: "success",
                    title: "Courrier généré",
                    message: tpl,
                  });
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12.5px] text-sf-text hover:bg-sf-primary-50"
              >
                <I.FileText size={13} className="text-sf-text-muted" />
                {tpl}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
